import { useId } from 'react';
import { SYMBOL_PATH } from '../../brand';

type AimdocLoaderProps = {
  label?: string;
};

/**
 * Full-screen loading state: the brand symbol with a light sheen passing
 * across it, over a hairline indeterminate track. The whole thing fades in
 * after a short delay so fast loads never flash it.
 */
export default function AimdocLoader({ label = 'Loading' }: AimdocLoaderProps) {
  const id = useId().replace(/:/g, '');
  const clipId = `aimdoc-loader-clip-${id}`;
  const sheenId = `aimdoc-loader-sheen-${id}`;

  return (
    <div
      className="aimdoc-loader fixed inset-0 z-50 flex items-center justify-center bg-background/70 backdrop-blur-sm"
      role="status"
      aria-label={label}
    >
      <div className="flex flex-col items-center gap-6">
        <svg viewBox="0 0 64 64" className="aimdoc-loader-symbol h-12 w-12" aria-hidden="true">
          <defs>
            <clipPath id={clipId}>
              <path d={SYMBOL_PATH} fillRule="evenodd" clipRule="evenodd" />
            </clipPath>
            <linearGradient id={sheenId} x1="0" y1="0" x2="1" y2="0">
              <stop offset="0" stopColor="#fff" stopOpacity="0" />
              <stop offset="0.5" stopColor="#fff" stopOpacity="0.55" />
              <stop offset="1" stopColor="#fff" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={SYMBOL_PATH} fillRule="evenodd" className="fill-[#1173F0] dark:fill-[#3B8BFF]" />
          <g clipPath={`url(#${clipId})`}>
            <rect className="aimdoc-loader-sheen" x="-40" y="-8" width="28" height="80" fill={`url(#${sheenId})`} />
          </g>
        </svg>
        <div className="relative h-[2px] w-20 overflow-hidden rounded-full bg-foreground/[0.08]">
          <div className="aimdoc-loader-track absolute inset-y-0 left-0 w-1/3 rounded-full bg-[#1173F0] dark:bg-[#3B8BFF]" />
        </div>
      </div>
      <span className="sr-only">{label}</span>
    </div>
  );
}
